import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import Container from '../../../../components/Container';
import AlertMsg from '../../../../components/AlertMsg';
import RestaurantHeader from '../../../../components/RestaurantHeader';

import { CouponList } from '../components';
import {
  useGetCoupons,
  useDeleteCoupon,
  useUpdateCoupon,
} from '../services';

const CouponsPage = () => {
  const { rest_id: restaurantId } = useParams<{ rest_id: string }>();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });
  const [togglingCouponId, setTogglingCouponId] = useState<string | null>(null);

  const { data: couponsData, isLoading, isError, error, refetch } = useGetCoupons(restaurantId || '', page, limit);

  const { mutate: deleteCoupon } = useDeleteCoupon(restaurantId || '');
  const { mutate: updateCoupon } = useUpdateCoupon(restaurantId || '');

  const handleDelete = (id: string) => {
    if (!window.confirm('Delete this coupon? This action cannot be undone.')) return;

    deleteCoupon(id, {
      onSuccess: () => {
        setMsgAlert({ open: true, message: 'Coupon deleted', msgType: 'success' });
      },
      onError: () => {
        setMsgAlert({ open: true, message: 'Failed to delete coupon', msgType: 'error' });
      },
    });
  };

  const handleToggleStatus = (id: string) => {
    const coupon = couponsData?.rows.find((c) => c._id === id);
    if (!coupon) return;

    setTogglingCouponId(id);
    const newStatus = coupon.status === 'active' ? 'disabled' : 'active';
    updateCoupon(
      { id, data: { status: newStatus } },
      {
        onSuccess: () => {
          setMsgAlert({ open: true, message: `Coupon ${newStatus === 'active' ? 'activated' : 'disabled'}`, msgType: 'success' });
          setTogglingCouponId(null);
        },
        onError: () => {
          setMsgAlert({ open: true, message: 'Failed to update status', msgType: 'error' });
          setTogglingCouponId(null);
        },
      }
    );
  };

  return (
    <Container page="restaurants" searchVal="" onSearch={() => null} searchLabel="">
      <Grid container item xs={12}>
        <Grid item xs={12} mb={2}>
          <RestaurantHeader
            title="Coupons"
            onBackBtnClick={() => navigate(`/restaurant/${restaurantId}`)}
          />
        </Grid>

        <Grid item xs={12} sx={styles.mainView} px={3} pt={3}>
          <Box>
            {isError ? (
              <Box sx={styles.errorContainer}>
                <Typography sx={styles.errorTitle}>Failed to load coupons</Typography>
                <Typography sx={styles.errorMessage}>
                  {(error as any)?.response?.data?.msg || 'An error occurred while fetching coupons. Please try again.'}
                </Typography>
                <Button variant="contained" onClick={() => refetch()} sx={styles.retryButton}>
                  Retry
                </Button>
              </Box>
            ) : (
              <CouponList
                coupons={couponsData?.rows || []}
                totalCount={couponsData?.count || 0}
                page={page}
                limit={limit}
                onPageChange={setPage}
                isLoading={isLoading}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                togglingCouponId={togglingCouponId}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ ...msgAlert, open: false })}
      />
    </Container>
  );
};

export default CouponsPage;

const styles = {
  mainView: {
    backgroundColor: '#FFF',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 200px)',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    minHeight: '300px',
  },
  errorTitle: {
    fontFamily: 'GilroySemiBold',
    fontSize: 18,
    color: '#d32f2f',
    marginBottom: '0.5rem',
  },
  errorMessage: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
    marginBottom: '1.5rem',
    textAlign: 'center',
    maxWidth: '500px',
  },
  retryButton: {
    backgroundColor: '#1a1a1a',
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    textTransform: 'none' as const,
    '&:hover': { backgroundColor: '#333' },
  },
};
